import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Map from "./pets/Map";

const Modal = ({
  show,
  onClose,
  title,
  children,
  footer,
  showCloseButton = true,
  size = "default",
}) => {
  if (!show) return null;

  const getSizeClasses = () => {
    switch (size) {
      case "large":
        return "max-w-5xl w-full h-[83vh]";
      case "small":
        return "max-w-sm w-full";
      default:
        return "max-w-md w-full";
    }
  };

  return (
    <div className="fixed inset-0 z-[99] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`bg-white rounded-lg shadow-lg text-center relative ${getSizeClasses()} flex flex-col`}
      >
        {(title || showCloseButton) && (
          <div className="flex justify-between items-center p-6 border-b">
            {title && (
              <h2 className="font-main font-semibold text-2xl text-orange-600">{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faX} className="text-xl" />
              </button>
            )}
          </div>
        )}

        <div
          className={`${title || showCloseButton ? "p-6" : "p-8"} ${
            size === "large" ? "flex-1 overflow-auto" : ""
          }`}
        >
          {children}
        </div>

        {footer && <div className="border-t border-gray-200 p-6">{footer}</div>}
      </div>
    </div>
  );
};

export const FilterModal = ({
  show,
  onClose,
  onAddFilter,
  onCreatePublication,
}) => {
  return (
    <Modal show={show} onClose={onClose} title="¡Antes de continuar!">
      <p className="font-main mb-4 text-gray-700">
        Te recomendamos primero completar los filtros de la izquierda con los
        datos de la mascota. Así, cuando creemos la publicación, esos datos se
        pasarán automáticamente y ahorrarás tiempo.
      </p>
      <div className="flex sm:flex-row gap-3 justify-center">
        <button
          className="bg-orange-500 text-white px-6 py-2 rounded font-main  hover:bg-orange-700 transition"
          onClick={onAddFilter}
        >
          Agregar filtro
        </button>
        <button
          className="font-main  px-6 py-2 text-gray-500 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition"
          onClick={onCreatePublication}
        >
          Crear publicación
        </button>
      </div>
    </Modal>
  );
};

export const MapModal = ({
  show,
  onClose,
  onMarkerAdded,
  onSave,
  radius,
  lat,
  lng,
  onRadiusChange,
  title = "Seleccionar ubicación",
}) => {
  const hasSelectedLocation = lat && lng && lat !== 0 && lng !== 0;

  const handleSave = () => {
    if (onSave) {
      onSave(lat, lng, radius);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const footerButtons = (
    <div className="flex justify-end gap-3">
      <button
        onClick={handleCancel}
        className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-main"
      >
        Cancelar
      </button>
      <button
        onClick={handleSave}
        disabled={!hasSelectedLocation}
        className={`px-6 py-2 rounded-lg font-main  transition-colors ${
          hasSelectedLocation
            ? "bg-orange-500 text-white hover:bg-orange-600"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        Guardar
      </button>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={title}
      size="large"
      showCloseButton={true}
      footer={footerButtons}
    >
      <div className="h-full flex flex-col">
        <Map
          onMarkerAdded={onMarkerAdded}
          radius={radius}
          zoom={13}
          lat={lat}
          lng={lng}
          height={"50vh"}
        />

        {!hasSelectedLocation && (
          <div className="mt-4 text-center">
            <p className="font-main text-gray-600 text-sm">
              📍 Haz clic en el mapa para seleccionar una ubicación
            </p>
          </div>
        )}

        {hasSelectedLocation && (
          <div className="mt-4 text-center">
            <label
              className="font-main text-sm  text-gray-700"
              htmlFor="radius-input"
            >
              Selecciona el radio de búsqueda: {radius}m
            </label>
            <input
              type="range"
              min="1000"
              max="10000"
              value={radius}
              step="1000"
              onChange={onRadiusChange}
              className="w-full mt-2"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1km</span>
              <span>10km</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export const ConfirmStateChangeModal = ({
  show,
  onClose,
  onConfirm,
  petName,
}) => {
  const footerButtons = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium font-main"
      >
        No actualizar estado
      </button>
      <button
        onClick={onConfirm}
        className="px-6 py-2 rounded-lg font-main font-medium text-white bg-secondary hover:bg-orange-700 transition-colors"
      >
        Ya encontró su hogar
      </button>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Confirmar cambio de estado"
      showCloseButton={true}
      footer={footerButtons}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <FontAwesomeIcon 
          icon={faExclamationTriangle} 
          className="text-orange-500 text-7xl"
        />
        <div className="text-center">
          <p className="font-main font-regular text-md text-gray-700 mb-3">
            Estás por confirmar que <strong className="font-main font-regular text-md">{petName}</strong> ya se reunió con su familia.
          </p>
          <p className="font-main text-md text-gray-600 mb-2">
            <strong className="font-main font-regular text-md">Esta acción no se puede deshacer.</strong>
          </p>
          <p className="font-main text-sm text-gray-500">
            La publicación de tu mascota cambiará al estado de "Recuperado" y ya no aparecerá en las búsquedas activas.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default Modal;